import { fetchApiGateway } from "@nova/fetch-api-gateway"
import { AircraftManualStatus, CreatableAircraft, UpdatableAircraft } from "@nova/schemas"
import { getDisplayableAircraftData } from "@nova/constants"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import { invalidateAircraftStatusCache } from "features/pilot-tracking/aircraft/AircraftStatusQueries"
import { invalidatePilotTrackingQuery } from "features/pilot-tracking/PilotTrackingQueries"
import { useAppSelector } from "integrations/redux/hooks"
import useSWR, { mutate } from "swr"
import useSWRMutation from "swr/mutation"
import { defineQuery } from "@nova/queryable"
import { invalidateMaintenanceRecords } from "../maintenance/MaintenanceRecordQueries"

export const invalidateAircraftCache = () =>
    Promise.all([
        mutate((key) => Array.isArray(key) && key[0] === "aircraft"),
        invalidatePilotTrackingQuery("aircraft"),
    ])

const getAircraftForOrg = (orgId: string) =>
    fetchApiGateway("get /org/:orgId/fleet/aircraft", { params: { orgId } })

export const useAircraft = () => {
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const { data, isLoading } = useSWR(orgId ? ["aircraft", orgId] : null, () =>
        getAircraftForOrg(orgId!).then((aircraft) =>
            aircraft.toSorted((a, b) =>
                (a.nickname ?? a.model ?? "").localeCompare(b.nickname ?? b.model ?? "")
            )
        )
    )

    return { aircraft: data ?? [], isLoading }
}

const createAircraft = (orgId: string, body: CreatableAircraft) => {
    return fetchApiGateway("post /org/:orgId/fleet/aircraft", {
        body,
        params: { orgId },
    })
}

export const useCreateAircraft = () => {
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const { trigger, isMutating } = useSWRMutation(
        "create-aircraft",
        (_key, { arg }: { arg: CreatableAircraft }) =>
            orgId ? createAircraft(orgId, arg) : undefined,
        { onSuccess: invalidateAircraftCache }
    )

    return {
        createAircraft: trigger,
        isCreating: isMutating,
    }
}

const updateAircraft = (orgId: string, aircraftId: string, body: UpdatableAircraft) => {
    return fetchApiGateway("patch /org/:orgId/fleet/aircraft/:aircraftId", {
        body,
        params: { orgId, aircraftId },
    })
}

export const useUpdateAircraft = () => {
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const { trigger, isMutating } = useSWRMutation(
        "update-aircraft",
        (_key, { arg }: { arg: { aircraftId: string; data: UpdatableAircraft } }) =>
            orgId ? updateAircraft(orgId, arg.aircraftId, arg.data) : undefined,
        {
            onSuccess: () =>
                Promise.all([invalidateAircraftCache(), invalidateAircraftStatusCache()]),
        }
    )

    return {
        updateAircraft: trigger,
        isUpdating: isMutating,
    }
}

const updateAircraftStatus = (
    orgId: string,
    aircraftId: string,
    status: AircraftManualStatus,
    templateIds: string[]
) => {
    return fetchApiGateway("post /org/:orgId/fleet/aircraft/:aircraftId/status", {
        params: { orgId, aircraftId },
        body: { status, reset_template_ids: templateIds },
    })
}

export const useUpdateAircraftStatus = () => {
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const { trigger, isMutating } = useSWRMutation(
        "update-aircraft-status",
        (
            _key,
            {
                arg,
            }: { arg: { aircraftId: string; status: AircraftManualStatus; templateIds: string[] } }
        ) =>
            orgId
                ? updateAircraftStatus(orgId, arg.aircraftId, arg.status, arg.templateIds)
                : undefined,
        {
            onSuccess: () =>
                Promise.all([
                    invalidateAircraftCache(),
                    invalidateAircraftStatusCache(),
                    invalidateMaintenanceRecords(),
                ]),
        }
    )

    return {
        updateAircraftStatus: trigger,
        isUpdating: isMutating,
    }
}

const deleteAircraft = (orgId: string, aircraftId: string) => {
    return fetchApiGateway("delete /org/:orgId/fleet/aircraft/:aircraftId", {
        params: { orgId, aircraftId },
    })
}

export const useDeleteAircraft = () => {
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const { trigger, isMutating } = useSWRMutation(
        "delete-aircraft",
        (_key, { arg }: { arg: string }) => (orgId ? deleteAircraft(orgId, arg) : undefined),
        { onSuccess: invalidateAircraftCache }
    )

    return {
        deleteAircraft: trigger,
        isDeleting: isMutating,
    }
}

export const useAircraftNickname = (aircraftId: string) => {
    const { aircraft } = useAircraft()
    const match = aircraft.find((a) => a.id === aircraftId)
    if (!match) return ""
    const { displayName } = getDisplayableAircraftData(match)
    return displayName
}

const deleteAircraftBatch = (orgId: string, ids: string[]) => {
    return fetchApiGateway("delete /org/:orgId/fleet/aircraft", {
        params: { orgId },
        body: { ids },
    })
}

export const useDeleteAircraftBatch = () => {
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const { trigger, isMutating } = useSWRMutation(
        "delete-aircraft-batch",
        (_key, { arg }: { arg: string[] }) => (orgId ? deleteAircraftBatch(orgId, arg) : undefined),
        { onSuccess: invalidateAircraftCache }
    )

    return {
        deleteAircraftBatch: trigger,
        isDeleting: isMutating,
    }
}

export const aircraftQuery = defineQuery((aircraftId: string) => ({
    resource: "aircraft",
    select: [
        "id",
        "nickname",
        "manufacturer",
        "model",
        "warranty_expires_at",
        "serial_number",
        "notes",
        "registration_number",
        "purchased_at",
        "manual_status",
        "color",
    ],
    filters: [{ field: "id", op: "=", value: aircraftId }],
}))
